import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { GridContext } from '../Grid';

// `data-hovered` drives `group-data-[hovered=true]/row:*` because a CSS :hover can't span the section tables
// or follow focus.
const gridRowVariants = cva('group/row flex items-center', {
  variants: {
    spacing: {
      cozy: 'h-8',
      comfy: 'h-10',
      roomy: 'h-13',
    },
    color: {
      white: 'bg-white',
      grey: 'bg-grey-10',
      blue: 'bg-blue-10',
    },
  },
  defaultVariants: {
    spacing: 'cozy',
    color: 'white',
  },
});

/** Pixel heights matching the `spacing` variant's `h-*` classes above — the single source of truth for row-height math elsewhere (e.g. sticky row stacking offsets). */
const GRID_ROW_HEIGHT_PX: Record<NonNullable<VariantProps<typeof gridRowVariants>['spacing']>, number> = {
  cozy: 32,
  comfy: 40,
  roomy: 52,
};

type GridRowProps = React.ComponentProps<'tr'> &
  VariantProps<typeof gridRowVariants> & { asChild?: boolean; fixedPinning?: boolean };

function GridRow({
  className,
  spacing,
  color = 'white',
  asChild = false,
  fixedPinning = false,
  ...props
}: GridRowProps) {
  const Comp = asChild ? Slot : 'tr';
  const gridContext = React.useContext(GridContext);
  const effectiveSpacing = spacing || gridContext.spacing || 'cozy';

  return (
    <Comp
      role="row"
      data-slot="grid-row"
      data-spacing={effectiveSpacing}
      data-color={color}
      className={cn(gridRowVariants({ spacing: effectiveSpacing, color, className }), fixedPinning ? 'pl-1' : '')}
      {...props}
    />
  );
}

export { GridRow, GRID_ROW_HEIGHT_PX, type GridRowProps };
